import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class SquareOrder extends ModelObject<SquareOrder> {

    public version: number = 0;
    /**
    * The Square unique id for this order (GUID).
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public squareOrderId: string = "";
    /**
    * The date and time this order was created in Square.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The order name as it would appear in Square (Source + Purchase Number).
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public orderName: string = "";
    /**
    * The total number of items in the order.
    */
    public totalOrderQuantity: number = 0;
    /**
    * The total net value of the order including delivery.
    */
    public totalNetPrice: number = 0;
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";
    private _lastErroredAt: Instant | null = null;
    /**
    * The date and time this order last errored while trying to import.
    */
    public get lastErroredAt(): Instant | null { return this._lastErroredAt }
    public set lastErroredAt(value: Instant | null) {
      this._lastErroredAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.squareOrderId = jsonSerializedObject.squareOrderId;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.orderName = jsonSerializedObject.orderName;
          this.totalOrderQuantity = jsonSerializedObject.totalOrderQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
          this.lastErroredAt = jsonSerializedObject.lastErroredAt == null ? null : Instant.parse(jsonSerializedObject.lastErroredAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.squareOrderId = this.squareOrderId;
        toRet.createdAt = this.createdAt.toString();
        toRet.orderName = this.orderName;
        toRet.totalOrderQuantity = this.totalOrderQuantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.errorMessage = this.errorMessage;
        toRet.lastErroredAt = this.lastErroredAt == null ? null : this.lastErroredAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "SquareOrder.BrewmanOrderId cannot be empty"},
    ];
}


export class FilteredSquareOrdersResult extends ModelObject<FilteredSquareOrdersResult> {

    public matchingCount: number = 0;
    public squareOrders: (SquareOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.squareOrders = jsonSerializedObject.squareOrders.map((v: any) => new SquareOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.squareOrders = this.squareOrders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "squareOrders", rule: (v: any) => !!v || "squareOrders is required"},
            {field: "squareOrders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SquareOrder[]).reduce((acc: Array<string>, curr: SquareOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SquareOrderFilter extends ModelObject<SquareOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _squareMerchantIdentifiers: readonly (string)[] | null = null;
    /**
    * Limit to specified square merchant store identifiers
    */
    public get squareMerchantIdentifiers(): readonly (string)[] | null { return this._squareMerchantIdentifiers }
    public set squareMerchantIdentifiers(value: readonly (string)[] | null) {
      this._squareMerchantIdentifiers = value === undefined ? null : value;
    }
    private _squareOrderIdentifiers: readonly (string)[] | null = null;
    /**
    * Limit to just orders with one of the given square-allocated ids.
    */
    public get squareOrderIdentifiers(): readonly (string)[] | null { return this._squareOrderIdentifiers }
    public set squareOrderIdentifiers(value: readonly (string)[] | null) {
      this._squareOrderIdentifiers = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.squareMerchantIdentifiers = jsonSerializedObject.squareMerchantIdentifiers == null ? null : jsonSerializedObject.squareMerchantIdentifiers.map((v: any) => v);
          this.squareOrderIdentifiers = jsonSerializedObject.squareOrderIdentifiers == null ? null : jsonSerializedObject.squareOrderIdentifiers.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.squareMerchantIdentifiers = this.squareMerchantIdentifiers == null ? null : this.squareMerchantIdentifiers.map(v => v);
        toRet.squareOrderIdentifiers = this.squareOrderIdentifiers == null ? null : this.squareOrderIdentifiers.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class SquareOrderService extends ServiceBase {

  public async getSquareOrdersByFilter(filter: SquareOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredSquareOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredSquareOrdersResult(response.results);;
    return this.makeJsonRequest("squareOrder/v1/getSquareOrdersByFilter", req, false, extractor, opts);
  };
}

export const squareOrderService = new SquareOrderService();